/**
 * Configuration.ts
 *
 * Module for logic relating to the sdk user configuration
 */

import * as Constants from "./../Constants";
import * as Localization from "./../Localization";
import * as Utils from "./../Utils";
import * as IInitOptions from "./IInitOptions";
import * as ILaunchOptions from "./ILaunchOptions";

import { AlignmentType, IOnSurveyActivatedCallback, ISurveyForm, ISurveyLauncher, ISurveyParams } from "@ms-ofb/floodgate-shared-typings";
import { IPersonalizationConfig, IPersonalizationSettings } from "@ms-ofb/officepersonalization/dist/lib/Common/Models";

import { TelemetrySink } from "@microsoft/oteljs";
import { ISurveyRenderContext } from "gatecrash";
import FeatureGateProvider from "../FloodgateCore/FeatureGateProvider";
import { PersonalizationLogger } from "../TargetedMessaging/PersonalizationLogger";
import { CentroFloodgateHostName } from "../UI/DynamicUx/CentroConstants";
import { CentroThemeDataSource } from '../UI/DynamicUx/CentroThemeDataSource';
import { Urls } from "../UI/UiConstants";
import { SDK_DEFAULT_PERSONALIZATIONSAMPLINGRATE } from "./../Constants";
import IUISurvey from "./../FloodgateCore/UISurvey/IUISurvey";
import { ICategoryOptions } from "./ICategoryOptions";

export * from "./IInitOptions";
export * from "./ILaunchOptions";

/**
 * Class representing a configuration (user provided options)
 */

export class Configuration {
	defaultAllowedDomains = [
		"microsoft.com",
		"office.com",
		"office.net",
		"office365.com",
		"microsoft365.com",
		"outlook.com",
		"live.com",
		"onedrive.com"
	];
	/**
	 * Validate category options, default "show" to false if CategoryOptions is not provided.
	 * @param categories Category options
	 * @returns {ICategoryOptions} the validated CategoryOptions
	 */
	private static validateCategoryOptions(categories?: ICategoryOptions): ICategoryOptions {
		if (!categories) {
			return { show: false };
		}

		Utils.expectBoolean(categories.show, "categories.show");
		if (categories.customCategories) {
			Utils.expectArray(categories.customCategories, "categories.customCategories");

			for (const customCategory of categories.customCategories) {
				Utils.expectString(customCategory, "custom category '" + customCategory + "'");
				if (customCategory.length > 20) {
					throw "custom category '" + customCategory + "' too long, max 20 characters.";
				}
			}
		}

		return categories;
	}

	private commonInitOptions: IInitOptions.IInitOptionsCommon;

	private inAppFeedbackInitOptions: IInitOptions.IInitOptionsInAppFeedback;
	private inAppFeedbackLaunchOptions: ILaunchOptions.ILaunchOptionsInAppFeedback;

	private floodgateInitOptions: IInitOptions.IInitOptionsFloodgate;
	private floodgateSurvey: IUISurvey;
	private cvRenderContext: ISurveyRenderContext;

	private personalizationInitOptions: IPersonalizationConfig;

	private sdkSessionId: string;

	private eventSampling: IInitOptions.ISamplingInitOption;

	private commonPolicyAllowAADC: boolean;

	private feedbackSubmitted = false;

	private featureGateProvider: FeatureGateProvider;

	public getFeedbackSubmitted(): boolean {
		return this.feedbackSubmitted;
	}

	public setFeedbackSubmitted(value: boolean): void {
		this.feedbackSubmitted = value;
	}

	public getCommonInitOptions(): IInitOptions.IInitOptionsCommon {
		return this.commonInitOptions;
	}

	public getInAppFeedbackInitOptions(): IInitOptions.IInitOptionsInAppFeedback {
		return this.inAppFeedbackInitOptions;
	}

	public getInAppFeedbackLaunchOptions(): ILaunchOptions.ILaunchOptionsInAppFeedback {
		return this.inAppFeedbackLaunchOptions;
	}

	public getFloodgateInitOptions(): IInitOptions.IInitOptionsFloodgate {
		return this.floodgateInitOptions;
	}

	public getPersonalizationInitOptions(): IPersonalizationConfig {
		if (this.personalizationInitOptions === undefined) {
			this.createPersonalizationInitOptions();
		}
		return this.personalizationInitOptions;
	}

	public getFloodgateSurvey(): IUISurvey {
		return this.floodgateSurvey;
	}

	public getCVRenderContext(): ISurveyRenderContext {
		return this.cvRenderContext;
	}

	public getSdkSessionId(): string {
		if (!this.sdkSessionId) {
			this.sdkSessionId = Utils.guid();
		}

		return this.sdkSessionId;
	}

	public getEventSampling(): IInitOptions.ISamplingInitOption {
		return this.eventSampling;
	}

	/**
	 * Set initOptions for common functionality.
	 */
	public setCommonInitOptions(passedInitOptions: IInitOptions.IInitOptionsCommon) {
		Utils.expectObject(passedInitOptions, "passedInitOptions");
		const initOptions: IInitOptions.IInitOptionsCommon = {
			appId: passedInitOptions.appId,
			applicationGroup: passedInitOptions.applicationGroup || {},
			build: passedInitOptions.build,
			cid: passedInitOptions.cid,
			environment: passedInitOptions.environment,
			intlFilename: passedInitOptions.intlFilename,
			intlUrl: passedInitOptions.intlUrl,
			webSurfacesResourceBaseUrl: passedInitOptions.webSurfacesResourceBaseUrl,
			locale: passedInitOptions.locale,
			onError: passedInitOptions.onError,
			originalLocale: passedInitOptions.locale,
			primaryColour: passedInitOptions.primaryColour,
			secondaryColour: passedInitOptions.secondaryColour,
			sessionId: passedInitOptions.sessionId,
			stylesUrl: passedInitOptions.stylesUrl,
			telemetryGroup: passedInitOptions.telemetryGroup || {},
			userEmail: passedInitOptions.userEmail,
			webGroup: passedInitOptions.webGroup || {},
			eventSampling: passedInitOptions.eventSampling,
			petrolTimeout: passedInitOptions.petrolTimeout,
			isCommercialHost: passedInitOptions.isCommercialHost,
			customResourcesSetExternally: passedInitOptions.customResourcesSetExternally,
			emailPolicyValue: passedInitOptions.emailPolicyValue,
			screenshotPolicyValue: passedInitOptions.screenshotPolicyValue,
			ageGroup: passedInitOptions.ageGroup,
			authenticationType: passedInitOptions.authenticationType,
			applicationSettings: passedInitOptions.applicationSettings,
			webSurfacesSettings: passedInitOptions.webSurfacesSettings,
			privacyUrl: passedInitOptions.privacyUrl || Urls.PrivacyStatementLink,
			retentionDurationDays: passedInitOptions.retentionDurationDays,
			telemetrySink: passedInitOptions.telemetrySink,
			userfactConfig: passedInitOptions.userfactConfig,
			personalizationSettings: passedInitOptions.personalizationSettings,
			allowedDomains: passedInitOptions.allowedDomains,
		};

		Utils.expectNumber(initOptions.appId, "initOptions.appId");
		if (!(initOptions.appId % 1 === 0 && initOptions.appId > 0)) {
			throw "initOptions.appId is not a positive integer: " + initOptions.appId;
		}

		// if defined, it should be a positive number
		if (initOptions.retentionDurationDays !== undefined) {
			Utils.expectNumber(initOptions.retentionDurationDays, "initOptions.retentionDurationDays");
			if (!(initOptions.retentionDurationDays % 1 === 0 && initOptions.retentionDurationDays > 0)) {
				throw "initOptions.retentionDurationDays is not a positive integer: " + initOptions.retentionDurationDays;
			}
		}

		const expectedValues = IInitOptions.CustomResources.Strings | IInitOptions.CustomResources.Css;

		if (initOptions.customResourcesSetExternally === undefined) {
			initOptions.customResourcesSetExternally = IInitOptions.CustomResources.None;
		} else if (typeof (initOptions.customResourcesSetExternally) !== "number" ||
			(initOptions.customResourcesSetExternally | expectedValues) !== expectedValues) {
			throw "initOptions.customResourcesSetExternally has an invalid value: " + initOptions.customResourcesSetExternally;
		}
		if ((initOptions.customResourcesSetExternally & IInitOptions.CustomResources.Css) === IInitOptions.CustomResources.Css || initOptions.stylesUrl) {
			const warnMessage = `WARNING: OfficeBrowserFeedback is no longer supporting the host application setting the styles for the surveys. 
			Please use the default styling.`;
			console.warn(warnMessage);
		}
		if ((initOptions.customResourcesSetExternally & IInitOptions.CustomResources.Strings) !== IInitOptions.CustomResources.Strings) {
			Utils.expectString(initOptions.intlUrl, "initOptions.intlUrl");
		}

		if (initOptions.intlFilename !== undefined) {
			Utils.expectString(initOptions.intlFilename, "initOptions.intlFilename");
		} else {
			initOptions.intlFilename = "officebrowserfeedbackstrings.js";
		}

		Utils.expectNumber(initOptions.environment, "initOptions.environment");
		if (!(initOptions.environment === 1 || initOptions.environment === 0)) {
			throw "initOptions.environment has a bad value: " + initOptions.environment;
		}

		if (initOptions.userEmail !== undefined) {
			Utils.expectString(initOptions.userEmail, "initOptions.userEmail");
		} else {
			initOptions.userEmail = "";
		}

		if (initOptions.sessionId !== undefined) {
			Utils.expectString(initOptions.sessionId, "initOptions.sessionId");
		} else {
			initOptions.sessionId = "00000000-0000-0000-0000-000000000000";
		}

		// Copy sessionID over to telemetryGroup
		initOptions.telemetryGroup.processSessionId = initOptions.sessionId;

		if (initOptions.cid !== undefined) {
			Utils.expectString(initOptions.cid, "initOptions.cid");

			// Copy cid over to extendedManifestData of applicationGroup
			initOptions.applicationGroup.extendedManifestData = JSON.stringify({ cid: initOptions.cid });
		}

		if (initOptions.build !== undefined) {
			Utils.expectString(initOptions.build, "initOptions.build");
			const buildRegex = "^[0-9]{1,9}(\\.[0-9]{1,9})?(\\.[0-9]{1,9})?(\\.[0-9]{1,9})?$";
			if (!new RegExp(buildRegex).test(initOptions.build)) {
				initOptions.build = "0.0.0.0";
			}
		} else {
			initOptions.build = "0.0.0.0";
		}
		// Copy build over to telemetryGroup
		initOptions.telemetryGroup.officeBuild = initOptions.build;

		const hexColourRegex = new RegExp("^#[0-9a-f]{3}([0-9a-f]{3})?$", "i");
		if (initOptions.primaryColour !== undefined) {
			if (!hexColourRegex.test(initOptions.primaryColour)) {
				initOptions.primaryColour = undefined;
			}
		}

		if (initOptions.secondaryColour !== undefined) {
			if (!hexColourRegex.test(initOptions.secondaryColour)) {
				initOptions.secondaryColour = undefined;
			}
		}

		if (initOptions.locale !== undefined) {
			Utils.expectString(initOptions.locale, "initOptions.locale");
			initOptions.locale = Localization.validate(initOptions.locale);
		} else {
			initOptions.locale = "en";
		}

		if (initOptions.onError === undefined) {
			initOptions.onError = (err: string) => { return; };
		}

		if (initOptions.petrolTimeout !== undefined) {
			Utils.expectNumber(initOptions.petrolTimeout, "initOptions.petrolTimeout");
		}

		if (initOptions.authenticationType !== undefined) {
			Utils.expectEnum(initOptions.authenticationType, IInitOptions.AuthenticationType, "initOptions.authenticationType");
		} else {
			initOptions.authenticationType = IInitOptions.AuthenticationType.Unauthenticated;
		}

		// For AAD or Unauthenticated, ageGroup value is unknown, so it's safe to only parse for MSA
		if (initOptions.ageGroup !== undefined) {
			Utils.expectEnum(initOptions.ageGroup, IInitOptions.AgeGroup, "initOptions.ageGroup");
		} else {
			initOptions.ageGroup = IInitOptions.AgeGroup.Undefined;
		}

		this.eventSampling = this.parseEventSamplingOption(initOptions.eventSampling);

		this.commonInitOptions = initOptions;
		this.commonPolicyAllowAADC = this.determineAADCPolicy(initOptions.authenticationType, initOptions.ageGroup);

		if (initOptions.isCommercialHost === undefined || initOptions.isCommercialHost == null) {
			initOptions.isCommercialHost = false; // By default privacy consent is not shown. (From SDK's perspective)
		} else {
			Utils.expectBoolean(initOptions.isCommercialHost, "initOptions.isCommercialHost");
		}

		if (initOptions.emailPolicyValue === undefined) {
			initOptions.emailPolicyValue = Constants.OCPSValues.NotConfigured;
		} else if (typeof (initOptions.emailPolicyValue) !== "number" ||
			(initOptions.emailPolicyValue !== Constants.OCPSValues.NotConfigured &&
				initOptions.emailPolicyValue !== Constants.OCPSValues.Enabled &&
				initOptions.emailPolicyValue !== Constants.OCPSValues.Disabled)) {
			throw "initOptions.emailPolicyValue has an invalid value: " + initOptions.emailPolicyValue;
		}

		if (initOptions.screenshotPolicyValue === undefined) {
			initOptions.screenshotPolicyValue = Constants.OCPSValues.NotConfigured;
		} else if (typeof (initOptions.screenshotPolicyValue) !== "number" ||
			initOptions.screenshotPolicyValue !== Constants.OCPSValues.NotConfigured &&
			initOptions.screenshotPolicyValue !== Constants.OCPSValues.Enabled &&
			initOptions.screenshotPolicyValue !== Constants.OCPSValues.Disabled) {
			throw "initOptions.screenshotPolicyValue has an invalid value: " + initOptions.screenshotPolicyValue;
		}

		if (initOptions.privacyUrl !== undefined) {
			Utils.expectString(initOptions.privacyUrl, "initOptions.privacyUrl");
		} else {
			initOptions.privacyUrl = Urls.PrivacyStatementLink;
		}

		// Expecting telemetrySink is given
		Utils.expectObject(initOptions.telemetrySink, "initOptions.telemetrySink");

		if (initOptions.allowedDomains !== undefined) {
			Utils.expectArray(initOptions.allowedDomains, "initOptions.allowedDomains");
			initOptions.allowedDomains = [...this.defaultAllowedDomains.concat(initOptions.allowedDomains)]
		} else {
			initOptions.allowedDomains = this.defaultAllowedDomains;
		}
	}

	/**
	 * Set init options for inAppFeedback
	 */
	public setInAppFeedbackInitOptions(passedInitOptions: IInitOptions.IInitOptionsInAppFeedback) {
		Utils.expectObject(passedInitOptions, "passedInitOptions");

		const initOptions: IInitOptions.IInitOptionsInAppFeedback = {
			bugForm: passedInitOptions.bugForm,
			onDismiss: passedInitOptions.onDismiss,
			screenshot: passedInitOptions.screenshot,
			showEmailAddress: passedInitOptions.showEmailAddress,
			userEmail: passedInitOptions.userEmail,
			transitionEnabled: passedInitOptions.transitionEnabled,
			isShowThanks: passedInitOptions.isShowThanks,
			sendFeedbackPolicyValue: passedInitOptions.sendFeedbackPolicyValue,
			feedbackForumUrl: passedInitOptions.feedbackForumUrl !== undefined ?
				passedInitOptions.feedbackForumUrl :
				undefined,
			myFeedbackForumUrl: passedInitOptions.myFeedbackForumUrl !== undefined ?
				passedInitOptions.myFeedbackForumUrl :
				undefined,
		};

		if (initOptions.onDismiss === undefined) {
			initOptions.onDismiss = function (submitted: boolean) { return; };
		}

		if (initOptions.bugForm !== undefined) {
			Utils.expectBoolean(initOptions.bugForm, "initOptions.bugForm");
		} else {
			initOptions.bugForm = false;
		}

		if (initOptions.userEmail !== undefined) {
			Utils.expectString(initOptions.userEmail, "initOptions.userEmail");
		}

		if (initOptions.screenshot !== undefined) {
			Utils.expectBoolean(initOptions.screenshot, "initOptions.screenshot");
		} else {
			initOptions.screenshot = true;
		}

		if (initOptions.showEmailAddress !== undefined) {
			Utils.expectBoolean(initOptions.showEmailAddress, "initOptions.showEmailAddress");
		} else {
			initOptions.showEmailAddress = true;
		}

		if (initOptions.feedbackForumUrl !== undefined) {
			Utils.expectString(initOptions.feedbackForumUrl, "initOptions.feedbackForumUrl");
		} else {
			initOptions.feedbackForumUrl = undefined;
		}

		if (initOptions.isShowThanks !== undefined) {
			Utils.expectBoolean(initOptions.isShowThanks, "initOptions.isShowThanks");
		} else {
			initOptions.isShowThanks = true;
		}

		if (initOptions.myFeedbackForumUrl !== undefined) {
			Utils.expectString(initOptions.myFeedbackForumUrl, "initOptions.myFeedbackForumUrl");
		} else {
			initOptions.myFeedbackForumUrl = undefined;
		}

		if (initOptions.transitionEnabled === undefined) {
			initOptions.transitionEnabled = true;
		}

		if (initOptions.sendFeedbackPolicyValue === undefined) {
			initOptions.sendFeedbackPolicyValue = Constants.OCPSValues.NotConfigured;
		} else if (typeof (initOptions.sendFeedbackPolicyValue) !== "number" ||
			(initOptions.sendFeedbackPolicyValue !== Constants.OCPSValues.NotConfigured &&
				initOptions.sendFeedbackPolicyValue !== Constants.OCPSValues.Enabled &&
				initOptions.sendFeedbackPolicyValue !== Constants.OCPSValues.Disabled)) {
			throw "initOptions.sendFeedbackPolicyValue has an invalid value: " + initOptions.sendFeedbackPolicyValue;
		}

		this.inAppFeedbackInitOptions = initOptions;
	}

	/**
	 * Set launch options for inAppFeedback
	 */
	public setInAppFeedbackLaunchOptions(launchOptions?: ILaunchOptions.ILaunchOptionsInAppFeedback) {
		if (!this.commonInitOptions) {
			throw "commonInitOptions not set";
		}

		if (!launchOptions) {
			launchOptions = {};
		}

		// Override any setting done in LaunchOptions for the metadataGroups
		this.commonInitOptions.applicationGroup = Utils.overrideValues(launchOptions.applicationGroup,
			this.commonInitOptions.applicationGroup);
		this.commonInitOptions.telemetryGroup = Utils.overrideValues(launchOptions.telemetryGroup,
			this.commonInitOptions.telemetryGroup);
		this.commonInitOptions.webGroup = Utils.overrideValues(launchOptions.webGroup,
			this.commonInitOptions.webGroup);

		launchOptions.categories = Configuration.validateCategoryOptions(launchOptions.categories);

		this.inAppFeedbackLaunchOptions = launchOptions;
	}

	/**
	 * Set init options for floodgate
	 */
	public setFloodgateInitOptions(passedInitOptions: IInitOptions.IInitOptionsFloodgate) {
		Utils.expectObject(passedInitOptions, "passedInitOptions");

		const initOptions: IInitOptions.IInitOptionsFloodgate = {
			autoDismiss: passedInitOptions.autoDismiss,
			campaignDefinitions: passedInitOptions.campaignDefinitions,
			campaignFlights: passedInitOptions.campaignFlights,
			campaignQueryParameters: passedInitOptions.campaignQueryParameters,
			onDismiss: passedInitOptions.onDismiss,
			onSurveyActivatedCallback: passedInitOptions.onSurveyActivatedCallback,
			settingStorageCallback: passedInitOptions.settingStorageCallback,
			uIStringGetter: passedInitOptions.uIStringGetter,
			authTokenCallback: passedInitOptions.authTokenCallback,
			featureStatusCallback: passedInitOptions.featureStatusCallback,
			surveyEnabled: passedInitOptions.surveyEnabled,
			messagingEnabled: passedInitOptions.messagingEnabled,
			showEmailAddress: passedInitOptions.showEmailAddress,
			customerVoiceSurveyParams: passedInitOptions.customerVoiceSurveyParams,
			surveyCSSParams: passedInitOptions.surveyCSSParams,
			governanceServiceEnabled: passedInitOptions.governanceServiceEnabled,
			governanceServiceConfig: passedInitOptions.governanceServiceConfig,
			personalizerEnabled: passedInitOptions.personalizerEnabled,
			messagingServiceSettings: passedInitOptions.messagingServiceSettings,
			isPriorityEnabled: passedInitOptions.isPriorityEnabled,
			isEudbUxEnabled: passedInitOptions.isEudbUxEnabled,
			appGovernanceProvider: passedInitOptions.appGovernanceProvider,
			dynamicCampaignConfig: passedInitOptions.dynamicCampaignConfig,
			logExperimentTrigger: passedInitOptions.logExperimentTrigger,
			userFactsReadConfig: passedInitOptions.userFactsReadConfig,
			dynamicUxConfig: passedInitOptions.dynamicUxConfig,
			campaignTelemetrySamplingConfig: passedInitOptions.campaignTelemetrySamplingConfig,
			surveyContainer: passedInitOptions.surveyContainer,
		};

		if (initOptions.autoDismiss === undefined) {
			initOptions.autoDismiss = Constants.AutoDismissValues.NoAutoDismiss;
		}

		if (initOptions.onDismiss === undefined) {
			initOptions.onDismiss = (campaignId: string, submitted: boolean) => { return; };
		}

		if (initOptions.onSurveyActivatedCallback === undefined) {
			const defaultOnSurveyActivatedCallback: IOnSurveyActivatedCallback = {
				onSurveyActivated(launcher: ISurveyLauncher, survey?: ISurveyForm, surveyParams?: ISurveyParams): void {
					launcher.launch();
				},
			};

			initOptions.onSurveyActivatedCallback = defaultOnSurveyActivatedCallback;
		}

		if (initOptions.uIStringGetter === undefined) {
			initOptions.uIStringGetter = (str: string) => str; // By default return the string as is.
		}

		// Note: surveyEnabled and messagingEnabled are both used to determine Floodgate's capabilities for the user.
		if (initOptions.surveyEnabled === undefined) {
			initOptions.surveyEnabled = true; // By default surveys are enabled. (From SDK's perspective)
		} else {
			Utils.expectBoolean(initOptions.surveyEnabled, "surveyEnabled");
		}

		if (initOptions.messagingEnabled === undefined) {
			initOptions.messagingEnabled = true; // By default messaging is enabled. (From SDK's perspective)
		} else {
			Utils.expectBoolean(initOptions.messagingEnabled, "messagingEnabled");
		}

		if (initOptions.showEmailAddress !== undefined) {
			Utils.expectBoolean(initOptions.showEmailAddress, "initOptionsFloodgate.showEmailAddress");
		} else {
			initOptions.showEmailAddress = true;  // By default email field and collection is enabled. (From SDK's perspective)
		}

		if (initOptions.governanceServiceEnabled !== undefined) {
			Utils.expectBoolean(initOptions.governanceServiceEnabled, "initOptionsFloodgate.governanceServiceEnable");
		} else {
			initOptions.governanceServiceEnabled = true;  // By default governanceServiceEnable is enabled
		}

		if (initOptions.governanceServiceEnabled) {
			initOptions.governanceServiceConfig = this.parseGovernanceServiceConfig(
				initOptions.governanceServiceConfig !== undefined ?
					initOptions.governanceServiceConfig :
					{});
		}

		if (initOptions.customerVoiceSurveyParams === undefined) {
			initOptions.customerVoiceSurveyParams = { cvFlights: "", isCVSurveyEnabled: true, productName: "", uiHost: "", isCopilotLicenseEnabled: false };
		} else {
			if (Utils.isNOU(initOptions.customerVoiceSurveyParams.isCVSurveyEnabled)) {
				initOptions.customerVoiceSurveyParams.isCVSurveyEnabled = true;
			}
			if (Utils.isNOU(initOptions.customerVoiceSurveyParams.isCopilotLicenseEnabled)) {
				initOptions.customerVoiceSurveyParams.isCopilotLicenseEnabled = false;
			}
		}

		if (initOptions.personalizerEnabled !== undefined) {
			Utils.expectBoolean(initOptions.personalizerEnabled, "initOptionsFloodgate.personalizerEnabled");
		} else {
			initOptions.personalizerEnabled = false;  // By default personalizerEnabled is disabled
		}

		if (initOptions.isPriorityEnabled !== undefined) {
			Utils.expectBoolean(initOptions.isPriorityEnabled, "initOptionsFloodgate.isPriorityEnabled");
		} else {
			initOptions.isPriorityEnabled = true;  // By default isPriorityEnabled is enabled
		}

		if (initOptions.isEudbUxEnabled !== undefined) {
			Utils.expectBoolean(initOptions.isEudbUxEnabled, "initOptionsFloodgate.isEudbUxEnabled");
		} else {
			initOptions.isEudbUxEnabled = true;  // By default isEudbUxEnabled is enabled
		}

		if (initOptions.featureStatusCallback) {
			Utils.expectFunction(initOptions.featureStatusCallback.getStringFeatureStatus, "initOptions.featureStatusCallback.getStringFeatureStatus");
			Utils.expectFunction(initOptions.featureStatusCallback.getIntFeatureStatus, "initOptions.featureStatusCallback.getIntFeatureStatus");
			Utils.expectFunction(initOptions.featureStatusCallback.getBoolFeatureStatus, "initOptions.featureStatusCallback.getBoolFeatureStatus");
		}

		if (initOptions.surveyContainer !== undefined) {
			try {
				Utils.expectString(initOptions.surveyContainer, "initOptionsFloodgate.surveyContainer");
			} catch (e) {
				// If the surveyContainer is not a string, check for being an object
				Utils.expectObject(initOptions.surveyContainer, "initOptionsFloodgate.surveyContainer");
			}
		}

		initOptions.dynamicCampaignConfig = this.parseDynamicCampaignConfig(initOptions.dynamicCampaignConfig ?? { enabled: false });

		initOptions.dynamicUxConfig = this.parseDynamicUxConfig(initOptions.dynamicUxConfig ?? { enabled: false });

		// Setup feature gates provider
		this.featureGateProvider = new FeatureGateProvider(initOptions.featureStatusCallback);

		this.floodgateInitOptions = initOptions;
	}

	/**
	 * get personalization environment from userfactConfig. This allows the host to override what deployment of the service to call.
	 * default is prod.
	 */
	private getEnvironmentSetting() {
		const environmentOverride = this.commonInitOptions.userfactConfig?.environment?.toLowerCase();
		switch (environmentOverride) {
			case "int":
				return "int";
			case "edog":
				return "edog";
			default:
				return "prod";
		}
	}

	/**
	 * Set init options for Personalization
	 */
	private createPersonalizationInitOptions() {
		Utils.expectObject(this.commonInitOptions, "passedInitOptions");
		Utils.expectNumber(this.commonInitOptions.appId, "expected appId");

		// keep default sampling rate at 1% if userfactConfig objec is not present
		let samplingRate = SDK_DEFAULT_PERSONALIZATIONSAMPLINGRATE;

		//populate personalizationSetting.eventSamplingRate with personalizationEventSamplingRate if it is present
		if (this.commonInitOptions.userfactConfig?.personalizationEventSamplingRate) {
			samplingRate = this.commonInitOptions.userfactConfig.personalizationEventSamplingRate;
		}

		const personalizationSettings: IPersonalizationSettings = {
			appId: this.commonInitOptions.appId,
			environment: this.getEnvironmentSetting(),
			eventSamplingRate: samplingRate,

			// To be used for userFactsRequestTimeOut, userFactsCacheExpiration and other settings
			...this.commonInitOptions.personalizationSettings
		}

		const initOptions: IPersonalizationConfig = {
			logger: new PersonalizationLogger(),
			sessionId: this.commonInitOptions.sessionId,
			settings: personalizationSettings,
			userFactConfig: this.commonInitOptions.userfactConfig,
		}

		this.personalizationInitOptions = initOptions;
	}


	/**
	 * Set the floodgate survey
	 */
	public setFloodgateSurvey(survey: IUISurvey): void {
		this.floodgateSurvey = survey;
	}

	/**
	 * Set the Customer Voice render context
	 */
	public setCVRenderContext(context: ISurveyRenderContext): void {
		this.cvRenderContext = context;
	}

	// Checks if the surveyEnabled policy is true or false.
	public checkIfSurveysEnabled(): boolean {
		// If no floodgateInitOptions defined, return true for surveyEnabled
		const policyAllowSurvey = this.floodgateInitOptions ? this.floodgateInitOptions.surveyEnabled : true;
		return policyAllowSurvey && this.commonPolicyAllowAADC;
	}

	// Checks if the messagingEnabled policy is true or false.
	public checkIfMessagingEnabled(): boolean {
		// If no floodgateInitOptions defined, return true for messagingEnabled
		const policyAllowMessaging = this.floodgateInitOptions ? this.floodgateInitOptions.messagingEnabled : true;
		return policyAllowMessaging;
	}

	/**
	 * Return the flag the Feedback and Survey enabled by AADC.
	 */
	public isFeedbackSurveyEnabledByAADC(): boolean {
		return this.commonPolicyAllowAADC;
	}

	public getCVSurveyFlights(): string | null {
		return this.floodgateInitOptions && this.floodgateInitOptions.customerVoiceSurveyParams ?
			this.floodgateInitOptions.customerVoiceSurveyParams.cvFlights : "";
	}

	public isCVSurveyEnabled(): boolean {
		return this.floodgateInitOptions && this.floodgateInitOptions.customerVoiceSurveyParams ?
			this.floodgateInitOptions.customerVoiceSurveyParams.isCVSurveyEnabled : true;
	}

	public getProductName(): string {
		return this.floodgateInitOptions && this.floodgateInitOptions.customerVoiceSurveyParams ?
			this.floodgateInitOptions.customerVoiceSurveyParams.productName : "";
	}

	public getUIHost(): string {
		return this.floodgateInitOptions && this.floodgateInitOptions.customerVoiceSurveyParams ?
			this.floodgateInitOptions.customerVoiceSurveyParams.uiHost : "";
	}

	public isCopilotLicenseEnabled(): boolean {
		return this.floodgateInitOptions && this.floodgateInitOptions.customerVoiceSurveyParams ?
			this.floodgateInitOptions.customerVoiceSurveyParams.isCopilotLicenseEnabled : false;
	}

	public getUIHostCVSurveyDispatcher(): string {
		return this.floodgateInitOptions && this.floodgateInitOptions.customerVoiceSurveyParams ?
			this.floodgateInitOptions.customerVoiceSurveyParams.uiHostCVSurveyDispatcher : "";
	}

	public getCSSPosition(): string {
		return this.floodgateInitOptions &&
			this.floodgateInitOptions.surveyCSSParams &&
			this.floodgateInitOptions.surveyCSSParams.cssPosition ?
			this.floodgateInitOptions.surveyCSSParams.cssPosition :
			"";
	}

	public getCSSZIndex(): string {
		return this.floodgateInitOptions &&
			this.floodgateInitOptions.surveyCSSParams &&
			this.floodgateInitOptions.surveyCSSParams.cssZIndex ?
			this.floodgateInitOptions.surveyCSSParams.cssZIndex :
			"";
	}

	public getCSSBottom(): string {
		return this.floodgateInitOptions &&
			this.floodgateInitOptions.surveyCSSParams &&
			this.floodgateInitOptions.surveyCSSParams.cssBottom ?
			this.floodgateInitOptions.surveyCSSParams.cssBottom :
			"";
	}

	public getCSSRight(): string {
		return this.floodgateInitOptions &&
			this.floodgateInitOptions.surveyCSSParams &&
			this.floodgateInitOptions.surveyCSSParams.cssRight ?
			this.floodgateInitOptions.surveyCSSParams.cssRight :
			"";
	}

	public getRuntimeCssVars(): { [key: string]: string } {
		return this.floodgateInitOptions?.surveyCSSParams?.runtimeCssVars ?? {};
	}

	public getRuntimeHtmlDataAttrs(): { [key: string]: string } {
		return this.floodgateInitOptions?.surveyCSSParams?.runtimeHtmlDataAttrs ?? {};
	}

	public getCSSVarString(): string {
		const styleSubstrings: string[] = [];
		if (this.floodgateInitOptions && this.floodgateInitOptions.surveyCSSParams) {
			if (this.floodgateInitOptions.surveyCSSParams.cssPosition) {
				styleSubstrings.push("--obf-position:", this.floodgateInitOptions.surveyCSSParams.cssPosition, ";");
			}
			if (this.floodgateInitOptions.surveyCSSParams.cssZIndex) {
				styleSubstrings.push("--obf-zIndex:", this.floodgateInitOptions.surveyCSSParams.cssZIndex, ";");
			}
			if (this.floodgateInitOptions.surveyCSSParams.cssBottom) {
				styleSubstrings.push("--obf-bottom:", this.floodgateInitOptions.surveyCSSParams.cssBottom, ";");
			}
			if (this.floodgateInitOptions.surveyCSSParams.cssRight) {
				styleSubstrings.push("--obf-right:", this.floodgateInitOptions.surveyCSSParams.cssRight, ";");
			}
		}
		return styleSubstrings.join("");
	}

	public getDynamicUxConfig(): IInitOptions.IDynamicUxConfig {
		return this.floodgateInitOptions?.dynamicUxConfig;
	}

	private setDynamicUxThemeConfig(themeConfig: IInitOptions.IDynamicUxThemeConfig): void {
		if (this.floodgateInitOptions?.dynamicUxConfig) {
			this.floodgateInitOptions.dynamicUxConfig.theme = themeConfig.theme;
			this.floodgateInitOptions.dynamicUxConfig.customTheme = themeConfig.customTheme ?? undefined;
		}
	}

	/**
	 * Reset the dynamicUxConfig to disable the feature
	 */
	public resetDynamicUxConfig(): void {
		if (this.floodgateInitOptions?.dynamicUxConfig) {
			this.floodgateInitOptions.dynamicUxConfig = {
				enabled: false,
				cdnStringConfig: {
					uiStringsCdnEnabled: true
				}
			};
		}
	}

	/**
	 * Check AADC rule at once since Configuration cannot be set again.
	 * So that we don't need to repeat the same process over again.
	 */
	public determineAADCPolicy(authenticationType: IInitOptions.AuthenticationType,
		ageGroup: IInitOptions.AgeGroup): boolean {
		// Return Based on authentication first
		if (authenticationType === IInitOptions.AuthenticationType.Unauthenticated ||
			authenticationType === IInitOptions.AuthenticationType.AAD) {
			return true;
		}
		// Return based on AgeGroup
		return ageGroup !== IInitOptions.AgeGroup.MinorWithParentalConsent &&
			ageGroup !== IInitOptions.AgeGroup.MinorWithoutParentalConsent;
	}

	// Keep implementation in sync with %SRCROOT%\privacy\idconsentprovider\IdentityBasedConsentGroupProvider.cpp
	public getUserConsentGroup(): IInitOptions.UserConsentGroup {
		const authenticationType = this.commonInitOptions.authenticationType;
		if (authenticationType === IInitOptions.AuthenticationType.Unauthenticated ||
			authenticationType === IInitOptions.AuthenticationType.AAD) {
			return IInitOptions.UserConsentGroup.CanConsent;
		}

		const ageGroup = this.commonInitOptions.ageGroup;
		switch (ageGroup) {
			case IInitOptions.AgeGroup.Undefined:
				return IInitOptions.UserConsentGroup.CanConsent;
			case IInitOptions.AgeGroup.MinorWithoutParentalConsent:
				return IInitOptions.UserConsentGroup.CannotConsent;
			case IInitOptions.AgeGroup.MinorWithParentalConsent:
				return IInitOptions.UserConsentGroup.CannotConsent;
			case IInitOptions.AgeGroup.Adult:
				return IInitOptions.UserConsentGroup.CanConsent;
			case IInitOptions.AgeGroup.NotAdult:
				return IInitOptions.UserConsentGroup.PartialConsent;
			case IInitOptions.AgeGroup.MinorNoParentalConsentRequired:
				return IInitOptions.UserConsentGroup.CanConsent;
			default:
				return IInitOptions.UserConsentGroup.CannotConsent;
		}
	}

	/**
	 * get FeatureGateProvider
	 */
	public getFeatureGateProvider() {
		return this.featureGateProvider;
	}

	private parseEventSamplingOption(samplingOptions: IInitOptions.ISamplingEvent[]): IInitOptions.ISamplingInitOption {
		const eventInitSampling: IInitOptions.ISamplingInitOption = { event: {} };
		if (samplingOptions !== undefined) {
			// Iterating through init options
			for (const index in samplingOptions) {
				if (Object.prototype.hasOwnProperty.call(samplingOptions, index)) {
					// Check for correct Type
					switch (samplingOptions[index].type) {
						case "Event": {
							// Parsing the Name and SampleRate
							const sampleRate = samplingOptions[index].sampleRate;
							Utils.expectNumber(sampleRate, "initOption.EventSampling.SampleRate");
							Utils.expectString(samplingOptions[index].name, "initOption.EventSampling.Name");
							// check the range for sampling percentage
							if (sampleRate < 0 || sampleRate > 1) {
								throw "initOption.EventSampling." + samplingOptions[index].name + ".SampleRate is out of range";
							}
							// finally, save the sampling event
							eventInitSampling.event[samplingOptions[index].name] = sampleRate;
							break;
						}
						default:
							throw "initOption.EventSampling.Type value is not recognized";
					}
				}
			}
		}
		return eventInitSampling;
	}

	private parseGovernanceServiceConfig(config: IInitOptions.IGovernanceServiceConfig): IInitOptions.IGovernanceServiceConfig {
		const defaultGovernanceServiceConfig: IInitOptions.IGovernanceServiceConfig = {
			// by default, enable triggering surveys in case of GovernanceService call failure
			forceServicelessSurveyDisplay: true,

			// by default, no retry
			retry: 1,

			// by default, timeout is 1000 miliseconds
			timeout: 1000,

			// SurveyLevelGovernance's default is false
			isSurveyLevelGovernanceEnabled: false,
		};

		if (config.forceServicelessSurveyDisplay !== undefined) {
			Utils.expectBoolean(config.forceServicelessSurveyDisplay, "initOptionsFloodgate.governanceServiceConfig.forceServicelessSurveyDisplay");
			defaultGovernanceServiceConfig.forceServicelessSurveyDisplay = config.forceServicelessSurveyDisplay;
		}

		if (config.retry !== undefined) {
			Utils.expectNumber(config.retry, "initOptionsFloodgate.governanceServiceConfig.retry");
			defaultGovernanceServiceConfig.retry = config.retry;
		}

		if (config.timeout !== undefined) {
			Utils.expectNumber(config.timeout, "initOptionsFloodgate.governanceServiceConfig.timeout");
			defaultGovernanceServiceConfig.timeout = config.timeout;
		}

		if (config.isSurveyLevelGovernanceEnabled !== undefined) {
			Utils.expectBoolean(config.isSurveyLevelGovernanceEnabled,
				"initOptionsFloodgate.governanceServiceConfig.isSurveyLevelGovernanceEnabled");
			defaultGovernanceServiceConfig.isSurveyLevelGovernanceEnabled = config.isSurveyLevelGovernanceEnabled;
		}

		return defaultGovernanceServiceConfig;
	}

	private parseDynamicCampaignConfig(config: IInitOptions.IDynamicCampaignConfig): IInitOptions.IDynamicCampaignConfig {
		const defaultDynamicCampaignConfig: IInitOptions.IDynamicCampaignConfig = {
			// Disabled by default
			enabled: false,

			ecsClient: undefined,
		};

		if (config.enabled !== undefined) {
			Utils.expectBoolean(config.enabled, "initOptionsFloodgate.dynamicCampaignConfig.enabled");
			defaultDynamicCampaignConfig.enabled = config.enabled;
		}

		if (config.ecsClient !== undefined) {
			Utils.expectObject(config.ecsClient, "initOptionsFloodgate.dynamicCampaignConfig.ecsClient");
			defaultDynamicCampaignConfig.ecsClient = config.ecsClient;
		}

		return defaultDynamicCampaignConfig;
	}

	private parseDynamicUxConfig(config: IInitOptions.IDynamicUxConfig): IInitOptions.IDynamicUxConfig {
		const defaultDynamicUxConfig: IInitOptions.IDynamicUxConfig = {
			// Disabled by default
			enabled: false,

			dynamicVariables: undefined,
		};

		if (config.enabled !== undefined) {
			Utils.expectBoolean(config.enabled, "initOptionsFloodgate.dynamicUxConfig.enabled");
			defaultDynamicUxConfig.enabled = config.enabled;
		} else {
			defaultDynamicUxConfig.enabled = false;
		}

		if (config.dynamicVariables !== undefined) {
			Utils.expectObject(config.dynamicVariables, "initOptionsFloodgate.dynamicUxConfig.dynamicVariables");
			Object.entries(config.dynamicVariables).forEach(([key, value]) => {
				Utils.expectString(key, "initOptionsFloodgate.dynamicUxConfig.dynamicVariables." + key);
				Utils.expectSimpleType(value, "initOptionsFloodgate.dynamicUxConfig.dynamicVariables[" + key + "]");
			});
			defaultDynamicUxConfig.dynamicVariables = config.dynamicVariables;
		}

		if (config.delayLoadingTimeout !== undefined) {
			Utils.expectNumber(config.delayLoadingTimeout, "initOptionsFloodgate.dynamicUxConfig.delayLoadingTimeout");
			defaultDynamicUxConfig.delayLoadingTimeout = config.delayLoadingTimeout;
		} else {
			defaultDynamicUxConfig.delayLoadingTimeout = 0;
		}

		const parsedThemeConfig = this.parseDynamicUxThemeConfig({
			theme: config.theme,
			customTheme: config.customTheme
		});
		defaultDynamicUxConfig.theme = parsedThemeConfig.theme;
		defaultDynamicUxConfig.customTheme = parsedThemeConfig.customTheme;

		if (config.footerButtonsAlignment !== undefined) {
			Utils.expectEnum(config.footerButtonsAlignment, AlignmentType, "initOptionsFloodgate.dynamicUxConfig.footerButtonsAlignment");
			defaultDynamicUxConfig.footerButtonsAlignment = config.footerButtonsAlignment;
		} else {
			defaultDynamicUxConfig.footerButtonsAlignment = AlignmentType.Left;
		}

		if (config.cdnStringConfig?.uiStringsCdnEnabled !== undefined) {
			Utils.expectBoolean(config.cdnStringConfig.uiStringsCdnEnabled, "initOptionsFloodgate.dynamicUxConfig.cdnStringConfig.uiStringsCdnEnabled");
			defaultDynamicUxConfig.cdnStringConfig = {
				uiStringsCdnEnabled: config.cdnStringConfig.uiStringsCdnEnabled
			};
		} else {
			defaultDynamicUxConfig.cdnStringConfig = {
				uiStringsCdnEnabled: true
			};
		}

		if (defaultDynamicUxConfig.cdnStringConfig.uiStringsCdnEnabled) {
			if (config.cdnStringConfig?.fetchCdnConcurrently !== undefined) {
				Utils.expectBoolean(config.cdnStringConfig.fetchCdnConcurrently, "initOptionsFloodgate.dynamicUxConfig.cdnStringConfig.fetchCdnConcurrently");
				defaultDynamicUxConfig.cdnStringConfig.fetchCdnConcurrently = config.cdnStringConfig.fetchCdnConcurrently;
			} else {
				defaultDynamicUxConfig.cdnStringConfig.fetchCdnConcurrently = true;
			}

			if (config.cdnStringConfig?.uiStringsLoadingTimeout !== undefined) {
				Utils.expectNumber(config.cdnStringConfig.uiStringsLoadingTimeout,
					"initOptionsFloodgate.dynamicUxConfig.cdnStringConfig.uiStringsLoadingTimeout");
				defaultDynamicUxConfig.cdnStringConfig.uiStringsLoadingTimeout = config.cdnStringConfig.uiStringsLoadingTimeout >= 0 ?
					config.cdnStringConfig.uiStringsLoadingTimeout : 3000;
			} else {
				defaultDynamicUxConfig.cdnStringConfig.uiStringsLoadingTimeout = 3000; // by default, timeout is 3000 miliseconds
			}

			if (config.cdnStringConfig?.uiStringCdnRetryLimit !== undefined) {
				Utils.expectNumber(config.cdnStringConfig.uiStringCdnRetryLimit,
					"initOptionsFloodgate.dynamicUxConfig.cdnStringConfig.uiStringCdnRetryLimit");
				defaultDynamicUxConfig.cdnStringConfig.uiStringCdnRetryLimit = config.cdnStringConfig.uiStringCdnRetryLimit >= 0 ?
					config.cdnStringConfig.uiStringCdnRetryLimit : 0;
			} else {
				defaultDynamicUxConfig.cdnStringConfig.uiStringCdnRetryLimit = 0; // by default, no retry is neeeded
			}
		}

		if (config.hostName !== undefined) {
			Utils.expectString(config.hostName, "initOptionsFloodgate.dynamicUxConfig.hostName")
			defaultDynamicUxConfig.hostName = config.hostName;
		} else {
			defaultDynamicUxConfig.hostName = CentroFloodgateHostName;
		}

		return defaultDynamicUxConfig;
	}
	/**
	* function to validate theme config for dynamic ux independently.
	* @param config theme config
	*/
	private parseDynamicUxThemeConfig(config: IInitOptions.IDynamicUxThemeConfig): IInitOptions.IDynamicUxThemeConfig {
		const defaultDynamicUxThemeConfig: IInitOptions.IDynamicUxThemeConfig = {};

		if (config.theme !== undefined) {
			Utils.expectString(config.theme, "initOptionsFloodgate.dynamicUxConfig.theme")
			defaultDynamicUxThemeConfig.theme = config.theme;
		} else {
			defaultDynamicUxThemeConfig.theme = Constants.CentroDefaultTheme;
		}

		if (config.customTheme !== undefined) {
			Utils.expectObject(config.customTheme, "initOptionsFloodgate.dynamicUxConfig.customTheme");
			defaultDynamicUxThemeConfig.customTheme = config.customTheme;
		} else {
			defaultDynamicUxThemeConfig.customTheme = undefined;
		}

		return defaultDynamicUxThemeConfig;
	}

	/**
	 * Updates the theme configuration of the SDK.
	 * @param themeConfig The new theme configuration.
	 * @param centroThemeDS The theme data source object.
	 * Throws an error if dynamic UX is not enabled or if themeConfig does not pass validation.
	 */
	public updateTheme(themeConfig: IInitOptions.IDynamicUxThemeConfig, centroThemeDS: CentroThemeDataSource): void {
		// If dynamic UX is not enabled, we indicate an error.
		if (!this.floodgateInitOptions.dynamicUxConfig?.enabled) {
			throw new Error("Failed to update theme configuration: dynamicUx not enabled");
		}

		let parsedThemeConfigResult: IInitOptions.IDynamicUxThemeConfig | null = null;

		try {
			// validate
			parsedThemeConfigResult = this.parseDynamicUxThemeConfig(themeConfig);
			// update configuration
			this.setDynamicUxThemeConfig(parsedThemeConfigResult);

			// Kick off the theme update on datasource for DynamicUx coupling side
			centroThemeDS.updateThemeState({
				baseTheme: parsedThemeConfigResult.theme,
				customTheme: parsedThemeConfigResult.customTheme,
			});
			return;
		} catch (error) {
			throw new Error("Failed to update theme configuration: " + error);
		}
	}

	/**
	 * Set the telemetry sink
	 * @param sinkObj The new telemetry Sink object
	 * Throws an error if sinkObj is not an object.
	 */
	public updateTelemetrySink(sinkObj: TelemetrySink): void {
		Utils.expectObject(sinkObj, "TelmetrySink.Object");
		this.commonInitOptions.telemetrySink = sinkObj;
	}

}

const configuration: Configuration = new Configuration();

/**
 * Get the current configuration
 */
export function get(): Configuration {
	return configuration;
}

/**
 * Validate feedbackType
 * @param feedbackType feedbackType
 * @returns {Constants.FeedbackType} the validated feedbackType
 */
export function validateFeedbackType(feedbackType: string): Constants.FeedbackType {
	Utils.expectString(feedbackType, "feedbackType");

	if (feedbackType === Constants.FeedbackType[Constants.FeedbackType.Smile]) {
		return Constants.FeedbackType.Smile;
	} else if (feedbackType === Constants.FeedbackType[Constants.FeedbackType.Frown]) {
		return Constants.FeedbackType.Frown;
	} else if (feedbackType === Constants.FeedbackType[Constants.FeedbackType.Bug]) {
		return Constants.FeedbackType.Bug;
	} else {
		throw "feedbackType should be one of Smile, Frown or Bug";
	}
}
